// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import type { Message, Source } from '@/types';
import type { UiStatePatch } from '@utils/chatRuntimeEvents';
import {
  clarificationChoiceBubbleFromCard,
  type ClarificationChoiceLabels,
} from '@utils/clarificationChoice';

// =============================================================================
// Runtime normalization helpers
// =============================================================================

export function toRuntimeRole(raw: unknown): Message['role'] | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.toLowerCase();
  if (normalized === 'user') return 'user';
  if (normalized === 'assistant') return 'assistant';
  if (normalized === 'system') return 'system';
  return null;
}

export function toRuntimeContent(raw: unknown): string {
  if (typeof raw === 'string') {
    return raw;
  }
  if (Array.isArray(raw)) {
    return raw
      .map((part) => {
        if (typeof part === 'string') return part;
        if (part && typeof part === 'object') {
          const text = (part as Record<string, unknown>).text;
          if (typeof text === 'string') return text;
        }
        return '';
      })
      .filter(Boolean)
      .join('\n')
      .trim();
  }
  if (raw && typeof raw === 'object') {
    const text = (raw as Record<string, unknown>).text;
    if (typeof text === 'string') return text;
  }
  return '';
}

// =============================================================================
// Assistant visibility / classification helpers
// =============================================================================

export function hasOnlyUserMessages(messages: Message[]): boolean {
  return messages.every((message) => message.role === 'user');
}

export function hasVisibleAssistantMessage(messages: Message[]): boolean {
  return messages.some((message) => {
    if (message.role !== 'assistant') {
      return false;
    }
    return Boolean(
      message.content.trim() ||
        message.ui_blocks?.length ||
        message.visualization_state ||
        ((message as unknown as Record<string, unknown>).artifacts_runtime as Record<
          string,
          unknown
        >)?.visualization_state
    );
  });
}

export function hasInteractiveSnapshotMetadata(message?: Message) {
  if (!message) return false;
  return Boolean(
    message.ui_blocks?.length ||
      message.visualization_state ||
      message.interactive ||
      message.latest_snapshot ||
      message.can_accept_action_patch
  );
}

export function hasRenderableAssistant(messages: Message[]): boolean {
  return messages.some(
    (message) =>
      message.role === 'assistant' &&
      Boolean(message.content.trim() || message.ui_blocks?.length || message.visualization_state)
  );
}

// =============================================================================
// Runtime ↔ store merge / preserve helpers
// =============================================================================

export function mergeRuntimeMessagesWithStoreMetadata(
  runtimeMessages: Message[],
  storeMessages: Message[]
): Message[] {
  if (runtimeMessages.length === 0 || storeMessages.length === 0) {
    return runtimeMessages;
  }
  const storedById = new Map(storeMessages.map((message) => [message.id, message]));
  return runtimeMessages.map((message) => {
    const stored = storedById.get(message.id);
    if (!stored) {
      return message;
    }
    return {
      ...stored,
      ...message,
      content: message.content || stored.content,
      timestamp: message.timestamp || stored.timestamp,
      thread_id: message.thread_id || stored.thread_id,
      ui_blocks: stored.ui_blocks?.length ? stored.ui_blocks : message.ui_blocks,
      visualization_state: stored.visualization_state ?? message.visualization_state,
    };
  });
}

export function storeMessagesExtendRuntimeTranscript(
  runtimeMessages: Message[],
  storeMessages: Message[]
) {
  if (runtimeMessages.length === 0 || storeMessages.length <= runtimeMessages.length) {
    return false;
  }

  const isRuntimePrefixOfStore = runtimeMessages.every((message, index) => {
    const candidate = storeMessages[index];
    return candidate && candidate.role === message.role && candidate.content === message.content;
  });

  if (!isRuntimePrefixOfStore) {
    return false;
  }

  return storeMessages.slice(runtimeMessages.length).some((message) => {
    if (message.role !== 'assistant') {
      return false;
    }
    return Boolean(
      message.content.trim() ||
        message.ui_blocks?.length ||
        message.visualization_state ||
        ((message as unknown as Record<string, unknown>).artifacts_runtime as Record<
          string,
          unknown
        >)?.visualization_state
    );
  });
}

export function storeMessagesRecoverPersistedAssistant(
  runtimeMessages: Message[],
  storeMessages: Message[]
) {
  if (!hasVisibleAssistantMessage(storeMessages) || storeMessages.length < runtimeMessages.length) {
    return false;
  }

  if (runtimeMessages.length === 0) {
    return true;
  }

  const rolesAligned = runtimeMessages.every(
    (message, index) => storeMessages[index]?.role === message.role
  );
  if (!rolesAligned) {
    return false;
  }

  return !hasVisibleAssistantMessage(runtimeMessages);
}

// =============================================================================
// Clarification choice bubbles (G7-N1)
// =============================================================================

/**
 * G7-N1：把用户澄清选择气泡插入 transcript（投影层派生，append-only 语义）。
 *
 * live 与恢复两条路径都经本 helper（同源派生，#185 防振荡）：live 由
 * `useMainChatMessageProjection` 从 `ui.clarification_cards` 派生；恢复由
 * `chatStore.protocol` 从 checkpoint 同名通道重建——同 id 幂等，双路径不产
 * 重复条目。气泡不进 CopilotKit message list、不随 RunAgentInput 回传。
 *
 * 插入点：最后一条 user 消息之后、该段**最后一个** assistant（resume 段首）
 * 之前——恢复条目间的非 assistant 边界使 `collapseSupersededAssistantTurns`
 * 的连续 run 判定天然不再跨中断合并（折叠函数本体不动）。
 *
 * 轮次归属（known-bounded）：只取**最新一张卡** + ≥2 个 assistant（中断前段
 * 与 resume 段同时在位）。无中断边界时（仅 1 个 assistant）不插——旧轮卡
 * 不会泄入单 assistant 的新轮。残余有界场景：更早 HITL 轮之后、当前轮为
 * 无中断多跳流式期间（≥2 assistant）可能瞬时插入并在 run 结束折叠后消失
 * （段级归属根治属 T3，不引入新 state 通道语义）。
 */
export function insertClarificationChoiceBubbles(
  messages: Message[],
  options: {
    cards: unknown[];
    hasResumeEvidence: boolean;
    labels: ClarificationChoiceLabels;
  }
): Message[] {
  const cards = Array.isArray(options.cards) ? options.cards : [];
  if (cards.length === 0 || !options.hasResumeEvidence) {
    return messages;
  }
  const latestCard = cards[cards.length - 1];
  const bubble = clarificationChoiceBubbleFromCard(latestCard, options.labels);
  if (!bubble) {
    return messages;
  }
  if (messages.some((message) => message.id === bubble.id)) {
    return messages;
  }

  let lastUserIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index].role === 'user') {
      lastUserIndex = index;
      break;
    }
  }
  if (lastUserIndex === -1) {
    return messages;
  }
  let insertIndex = -1;
  for (let index = messages.length - 1; index > lastUserIndex; index -= 1) {
    if (messages[index].role === 'assistant') {
      insertIndex = index;
      break;
    }
  }
  if (insertIndex === -1 || insertIndex - lastUserIndex < 2) {
    // 中断前段 + resume 段未同时在位（未答/单段轮）——不渲染选择气泡。
    return messages;
  }

  const next = [...messages];
  next.splice(insertIndex, 0, {
    id: bubble.id,
    role: 'user',
    content: bubble.content,
    timestamp: new Date(
      bubble.submittedAtMs > 0 ? bubble.submittedAtMs : Date.now()
    ).toISOString(),
    thread_id: next[insertIndex].thread_id,
  });
  return next;
}

// =============================================================================
// Turn collapse
// =============================================================================

export function collapseSupersededAssistantTurns(
  messages: Message[],
  options: {
    interactiveMessageId?: string | null;
    /** G7-H D13：run 在途时强制取连续 assistant 块的末段（"替换当前段"语义）。
     * react 多跳会产生多个 TEXT 段：中间段逐字播出后若被 preferred（interactive/
     * snapshot 标记）选中残留，用户会看到"长段播出后被删换"；流中只显示当前
     * 在流段、终态=末段消除最大观感伤害。根治（段级不进正文）属 T3。 */
    preferLatestWhileRunning?: boolean;
    /** G7-N8：pre-run assistant id 集合——这些是用户已读的历史完成段而非本
     * run 的 hop，作为折叠屏障（不被折叠掉、并切断连续 run），防止 resume/
     * 思考期草稿与历史段发生"旧段被删换"。 */
    protectedIds?: ReadonlySet<string>;
  } = {}
) {
  if (messages.length <= 1) {
    return messages;
  }

  const interactiveMessageId = options.interactiveMessageId?.trim();
  const preferLatestWhileRunning = Boolean(options.preferLatestWhileRunning);
  const protectedIds = options.protectedIds;
  const collapsed: Message[] = [];

  for (let index = 0; index < messages.length; ) {
    const message = messages[index];
    if (message.role !== 'assistant') {
      collapsed.push(message);
      index += 1;
      continue;
    }

    let end = index + 1;
    while (end < messages.length && messages[end].role === 'assistant') {
      end += 1;
    }

    const run = messages.slice(index, end);
    // G7-N8：屏障成员（pre-run 已读段）——含屏障成员的连续 run 整组保留、
    // 组内不折叠（方向安全=多保留而非误删；屏障后新 hop 的中途折叠豁免属
    // 可接受偏差）。
    if (protectedIds && protectedIds.size > 0) {
      const hasProtected = run.some((candidate) => protectedIds.has(candidate.id));
      if (hasProtected) {
        for (const candidate of run) {
          collapsed.push(candidate);
        }
        index = end;
        continue;
      }
    }
    if (run.length === 1) {
      collapsed.push(run[0]);
      index = end;
      continue;
    }

    const preferred =
      (interactiveMessageId
        ? run.find((candidate) => candidate.id === interactiveMessageId)
        : undefined) ??
      (preferLatestWhileRunning
        ? run[run.length - 1]
        : [...run]
            .reverse()
            .find((candidate) =>
              Boolean(
                candidate.latest_snapshot ||
                  candidate.can_accept_action_patch ||
                  candidate.ui_blocks?.length ||
                  candidate.visualization_state ||
                  candidate.interactive
              )
            ) ??
          run[run.length - 1]);

    collapsed.push(preferred);
    index = end;
  }

  return collapsed;
}

// =============================================================================
// Duplicate assistant content collapse
// =============================================================================

/**
 * Normalize message content for dedup comparison: trim whitespace,
 * collapse repeated line breaks, and strip trailing punctuation noise.
 */
function normalizeContentForDedup(content: string): string {
  return content
    .trim()
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[\s\u200B\u00A0]+$/g, '');
}

/**
 * Collapse consecutive assistant messages whose normalized content is
 * identical or where one is a strict prefix of the other (partial
 * duplicate).  When collapsing, keep the message with the longest
 * content; if content lengths are equal, keep the one with more
 * metadata (ui_blocks, visualization_state, interactive flags).
 *
 * This is NOT a UI-level filter — it operates on the projected message
 * data before rendering, and targets the root cause of duplicate
 * assistant responses that originate from backend crash‑retry
 * persistence cycles.
 */
export function collapseDuplicateAssistantContent(messages: Message[]): Message[] {
  if (messages.length <= 1) {
    return messages;
  }

  const collapsed: Message[] = [];

  for (let idx = 0; idx < messages.length; ) {
    const current = messages[idx];

    if (current.role !== 'assistant') {
      collapsed.push(current);
      idx += 1;
      continue;
    }

    // Find the contiguous run of assistant messages starting at idx.
    let runEnd = idx + 1;
    while (runEnd < messages.length && messages[runEnd].role === 'assistant') {
      runEnd += 1;
    }

    if (runEnd === idx + 1) {
      // Single assistant — nothing to collapse.
      collapsed.push(current);
      idx = runEnd;
      continue;
    }

    // Cluster assistants with duplicate/prefix content within this run.
    const run = messages.slice(idx, runEnd);
    const kept: Set<number> = new Set();
    const skip: Set<number> = new Set();

    for (let i = 0; i < run.length; i += 1) {
      if (skip.has(i)) continue;
      const contentI = normalizeContentForDedup(run[i].content);
      if (!contentI) {
        kept.add(i);
        continue;
      }

      let champion = i;
      for (let j = i + 1; j < run.length; j += 1) {
        if (skip.has(j)) continue;
        const contentJ = normalizeContentForDedup(run[j].content);
        if (!contentJ) {
          kept.add(j);
          continue;
        }

        const isPrefix = contentI.startsWith(contentJ) || contentJ.startsWith(contentI);
        const isExact = contentI === contentJ;
        const isDuplicate = isExact || isPrefix;

        if (!isDuplicate) continue;

        // Decide which one to keep.
        const lenI = run[champion].content.trim().length;
        const lenJ = run[j].content.trim().length;

        if (lenJ > lenI) {
          champion = j;
          skip.add(i);
          break;
        }

        const metaWeight = (candidate: Message): number => {
          let w = 0;
          if (candidate.ui_blocks?.length) w += 2;
          if (candidate.visualization_state) w += 2;
          if (candidate.interactive) w += 1;
          if (candidate.latest_snapshot) w += 1;
          if (candidate.thinking_trace) w += 1;
          return w;
        };

        if (lenI === lenJ && metaWeight(run[j]) > metaWeight(run[champion])) {
          champion = j;
        }

        skip.add(j);
      }

      kept.add(champion);
    }

    for (let i = 0; i < run.length; i += 1) {
      if (kept.has(i)) {
        collapsed.push(run[i]);
      }
    }

    idx = runEnd;
  }

  return collapsed;
}

// =============================================================================
// Shape comparison
// =============================================================================

export function haveSameTranscriptShape(left: Message[], right: Message[]) {
  if (left.length !== right.length) return false;
  return left.every((message, index) => {
    const candidate = right[index];
    return (
      message.id === candidate.id &&
      message.role === candidate.role &&
      message.content === candidate.content
    );
  });
}

/**
 * 消息语义相等的单一事实源在 `@utils/transcriptMessageSemantics`（store 层也需要
 * 同一个口径，不能反向 import 本页面模块）。这里保留同名导出，既有调用点不变，
 * 语义已扩展为逐字段值比较（含 ui_blocks payload、thinking_trace 内容与状态、
 * action_revision 等），不再以数量代替内容。
 */
export { haveSameMessageShape } from '@utils/transcriptMessageSemantics';

// =============================================================================
// Store-side merge / preserve
// =============================================================================

export function shouldPreserveRicherMessages(existing: Message[], incoming: Message[]): boolean {
  if (existing.length === 0 || !hasRenderableAssistant(existing)) {
    return false;
  }

  if (incoming.length === 0) {
    return true;
  }

  const rolesAligned = incoming.every((message, index) => existing[index]?.role === message.role);
  if (!rolesAligned) {
    return false;
  }

  if (!hasRenderableAssistant(incoming)) {
    return incoming.length <= existing.length;
  }

  if (incoming.length !== existing.length) {
    return false;
  }

  return incoming.some((message, index) => {
    if (message.role !== 'assistant') {
      return false;
    }
    const persisted = existing[index];
    if (!persisted || persisted.role !== 'assistant') {
      return false;
    }
    const incomingIsSparse =
      !message.content.trim() && !message.ui_blocks?.length && !message.visualization_state;
    const persistedIsRenderable = Boolean(
      persisted.content.trim() || persisted.ui_blocks?.length || persisted.visualization_state
    );
    return incomingIsSparse && persistedIsRenderable;
  });
}

function mergeRuntimeMessageSnapshot(existing: Message, incoming: Message): Message {
  const nextContent =
    incoming.content.trim() || !existing.content.trim() ? incoming.content : existing.content;

  return {
    ...existing,
    ...incoming,
    content: nextContent,
    thinking_duration_seconds:
      incoming.thinking_duration_seconds ?? existing.thinking_duration_seconds,
    thinking_trace: incoming.thinking_trace ?? existing.thinking_trace,
    ui_blocks: incoming.ui_blocks ?? existing.ui_blocks,
    visualization_state: incoming.visualization_state ?? existing.visualization_state,
    ui_generation_mode: incoming.ui_generation_mode ?? existing.ui_generation_mode,
    action_revision: incoming.action_revision ?? existing.action_revision,
    interactive: incoming.interactive ?? existing.interactive,
    latest_snapshot: incoming.latest_snapshot ?? existing.latest_snapshot,
    can_accept_action_patch: incoming.can_accept_action_patch ?? existing.can_accept_action_patch,
    feedback: incoming.feedback ?? existing.feedback,
    sources: incoming.sources ?? existing.sources,
    rag_status: incoming.rag_status ?? existing.rag_status,
    retrieval_mode: incoming.retrieval_mode ?? existing.retrieval_mode,
  };
}

export function mergeRuntimeMessagesIntoExisting(existing: Message[], incoming: Message[]): Message[] {
  if (existing.length === 0 || incoming.length === 0) {
    return incoming;
  }

  const existingById = new Map(existing.map((message) => [message.id, message]));
  const canMergeByIndex =
    existing.length === incoming.length &&
    incoming.every((message, index) => existing[index]?.role === message.role);

  return incoming.map((message, index) => {
    const persisted =
      existingById.get(message.id) ?? (canMergeByIndex ? existing[index] : undefined);
    if (!persisted || persisted.role !== message.role) {
      return message;
    }
    return mergeRuntimeMessageSnapshot(persisted, message);
  });
}

// =============================================================================
// Interactive assistant marking and enrichment
// =============================================================================

export function markLatestInteractiveAssistant(
  messages: Message[],
  options?: {
    interactiveMessageId?: string;
    actionRevision?: number;
  }
): Message[] {
  const interactiveMessageId = options?.interactiveMessageId?.trim();
  let targetIndex = -1;

  if (interactiveMessageId) {
    targetIndex = messages.findIndex(
      (message) => message.role === 'assistant' && message.id === interactiveMessageId
    );
  }

  if (targetIndex === -1) {
    for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
      if (
        messages[idx].role === 'assistant' &&
        (messages[idx].ui_blocks?.length || messages[idx].visualization_state)
      ) {
        targetIndex = idx;
        break;
      }
    }
  }

  if (targetIndex === -1) {
    return messages;
  }

  return messages.map((message, idx) => {
    if (message.role !== 'assistant') {
      return message;
    }
    const isTarget = idx === targetIndex;
    return {
      ...message,
      latest_snapshot: isTarget,
      can_accept_action_patch: isTarget,
      interactive:
        isTarget ||
        Boolean(message.interactive || message.ui_blocks?.length || message.visualization_state),
      action_revision: isTarget
        ? (options?.actionRevision ?? message.action_revision)
        : message.action_revision,
    };
  });
}

export function attachLatestAssistantUi(
  messages: Message[],
  uiBlocks?: Message['ui_blocks'],
  visualizationState?: Message['visualization_state'],
  uiGenerationMode?: Message['ui_generation_mode'],
  interactiveMessageId?: string,
  actionRevision?: number
): Message[] {
  if ((!uiBlocks || uiBlocks.length === 0) && !visualizationState && !uiGenerationMode) {
    return markLatestInteractiveAssistant(messages, {
      interactiveMessageId,
      actionRevision,
    });
  }

  for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
    if (messages[idx].role !== 'assistant') continue;
    const copy = [...messages];
    copy[idx] = {
      ...copy[idx],
      id: interactiveMessageId || copy[idx].id,
      ui_blocks: uiBlocks ?? copy[idx].ui_blocks,
      visualization_state: visualizationState ?? copy[idx].visualization_state,
      ui_generation_mode: uiGenerationMode ?? copy[idx].ui_generation_mode,
      interactive: true,
      latest_snapshot: true,
      can_accept_action_patch: true,
      action_revision: actionRevision ?? copy[idx].action_revision,
    };
    return markLatestInteractiveAssistant(copy, {
      interactiveMessageId: interactiveMessageId || copy[idx].id,
      actionRevision: actionRevision ?? copy[idx].action_revision,
    });
  }

  return messages;
}

export function attachLatestAssistantRag(
  messages: Message[],
  sources?: Source[],
  ragStatus?: string,
  retrievalMode?: string
): Message[] {
  if ((!sources || sources.length === 0) && !ragStatus && !retrievalMode) {
    return messages;
  }
  for (let idx = messages.length - 1; idx >= 0; idx -= 1) {
    if (messages[idx].role !== 'assistant') continue;
    const copy = [...messages];
    copy[idx] = {
      ...copy[idx],
      sources: sources ?? copy[idx].sources,
      rag_status: ragStatus ?? copy[idx].rag_status,
      retrieval_mode: retrievalMode ?? copy[idx].retrieval_mode,
    };
    return copy;
  }
  return messages;
}

// =============================================================================
// UI state patching
// =============================================================================

function normalizeDeltaForCompare(text: string): string {
  return text.replace(/\s+/g, '');
}

function mergeSolutionDelta(content: string, solutionDelta: unknown): string {
  if (typeof solutionDelta !== 'string' || !solutionDelta.trim()) {
    return content;
  }
  const trimmedDelta = solutionDelta.trim();
  if (!content.trim()) {
    return trimmedDelta;
  }
  // BUG-17：后端改写正文（如幻觉声明剥离）后，原始 includes 守卫会因空白/
  // markdown 差异假阴性，把已含注记再追加一遍产生重复片段。改为归一化
  // （压空白）后的包含判断；正文确实不含时照常追加（恢复单份，非重复）。
  if (normalizeDeltaForCompare(content).includes(normalizeDeltaForCompare(trimmedDelta))) {
    return content;
  }
  return `${content}\n\n${trimmedDelta}`;
}

export function applyUiStatePatchToMessages(
  messages: Message[],
  targetMessageId: string,
  patch?: UiStatePatch,
  scope?: { threadId: string; blockId: string },
): Message[] | null {
  const nextMessages = [...messages];
  let targetIndex = nextMessages.findIndex((message) =>
    message.role === 'assistant' && message.id === targetMessageId &&
    (!scope || message.thread_id === scope.threadId));
  if (targetIndex === -1 && scope) {
    // History may synthesize a local id while the server retains the persisted
    // interactive_message_id. A unique active card in the same thread/block is
    // the only safe fallback; never guess from a revision increment alone.
    const candidates = nextMessages.flatMap((message, index) =>
      message.role === 'assistant' && message.thread_id === scope.threadId &&
      message.latest_snapshot && message.can_accept_action_patch &&
      message.ui_blocks?.some((block) => block.id === scope.blockId)
        ? [index] : []);
    if (candidates.length === 1) targetIndex = candidates[0];
  }
  if (targetIndex === -1) {
    return null;
  }

  const localMessageId = nextMessages[targetIndex].id;
  nextMessages[targetIndex] = {
    ...nextMessages[targetIndex],
    id: localMessageId,
    content: mergeSolutionDelta(nextMessages[targetIndex].content, patch?.solutionDelta),
    ui_blocks: patch?.uiBlocks ?? nextMessages[targetIndex].ui_blocks,
    visualization_state:
      patch?.visualizationState && typeof patch.visualizationState === 'object'
        ? patch.visualizationState
        : nextMessages[targetIndex].visualization_state,
    ui_generation_mode: patch?.uiGenerationMode ?? nextMessages[targetIndex].ui_generation_mode,
    action_revision: patch?.actionRevision ?? nextMessages[targetIndex].action_revision,
    interactive: true,
    latest_snapshot: true,
    can_accept_action_patch: true,
  };

  return markLatestInteractiveAssistant(nextMessages, {
    interactiveMessageId: localMessageId,
    actionRevision: patch?.actionRevision ?? nextMessages[targetIndex].action_revision,
  });
}
